import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, ShieldCheck, Globe, Sparkles, ArrowUpRight, Loader2 } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import JoinDialog from "@/components/landing/JoinDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessDetail() {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/businesses/${id}`);
        if (cancelled) return;
        setBusiness(res.data);
        const relRes = await axios.get(`${API}/businesses`, { params: { category: res.data.category } });
        if (cancelled) return;
        setRelated(relRes.data.filter((b) => b.id !== id).slice(0, 3));
      } catch (e) {
        setBusiness(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-ink" data-testid="business-detail-page">
      <Header onJoin={() => setJoinOpen(true)} onNav={() => {}} />

      <main className="pt-8 pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-wine transition-colors"
            data-testid="business-back-link"
          >
            <ArrowLeft size={16} /> Back to Business Exchange
          </Link>

          {loading && (
            <div className="mt-16 text-brand-muted flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading business…
            </div>
          )}

          {!loading && !business && (
            <div className="mt-16 text-brand-muted">Business not found.</div>
          )}

          {!loading && business && (
            <>
              <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="lg:col-span-5"
                >
                  <div className="aspect-[4/5] rounded-[2rem] overflow-hidden border border-brand-line">
                    <img src={business.image} alt={business.name} className="w-full h-full object-cover" />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="lg:col-span-7"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine" data-testid="detail-tag">
                      {business.tag}
                    </span>
                    {business.verified && (
                      <span className="text-xs font-semibold text-brand-wine bg-brand-wine/10 border border-brand-wine/20 rounded-full px-3 py-1 flex items-center gap-1">
                        <ShieldCheck size={12} /> Verified
                      </span>
                    )}
                  </div>
                  <h1 className="mt-3 font-serif text-5xl md:text-6xl font-medium leading-[1.05] text-brand-ink" data-testid="detail-name">
                    {business.name}
                  </h1>
                  {business.tagline && (
                    <p className="mt-4 font-serif italic text-2xl text-brand-wine leading-snug">
                      &ldquo;{business.tagline}&rdquo;
                    </p>
                  )}
                  <p className="mt-5 text-brand-muted text-lg leading-relaxed" data-testid="detail-description">
                    {business.description}
                  </p>

                  <div className="mt-6 flex items-center gap-6 text-sm text-brand-muted flex-wrap">
                    <span className="flex items-center gap-2"><MapPin size={14} /> {business.location}</span>
                    {business.website && (
                      <a href={business.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-brand-wine" data-testid="detail-website">
                        <Globe size={14} /> Website <ArrowUpRight size={12} />
                      </a>
                    )}
                    {business.readiness_score !== null && business.readiness_score !== undefined && (
                      <span className="flex items-center gap-2 text-brand-wine font-medium">
                        <Sparkles size={14} /> Readiness {business.readiness_score}/100
                      </span>
                    )}
                  </div>

                  {business.capabilities?.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine mb-3">
                        Capabilities
                      </h3>
                      <div className="flex flex-wrap gap-2" data-testid="detail-capabilities">
                        {business.capabilities.map((c) => (
                          <span key={c} className="bg-white border border-brand-line rounded-full px-4 py-2 text-sm text-brand-ink">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-10 flex flex-wrap gap-3">
                    <a
                      href={`mailto:hello@yuukke.com?subject=Sourcing enquiry: ${encodeURIComponent(business.name)}`}
                      className="bg-brand-wine text-white rounded-lg px-7 py-3.5 font-medium hover:bg-brand-wineDark transition-colors"
                      data-testid="detail-contact-btn"
                    >
                      Request an intro
                    </a>
                    <button
                      onClick={() => setJoinOpen(true)}
                      className="bg-white border border-brand-wine text-brand-wine rounded-lg px-7 py-3.5 font-medium hover:bg-brand-wine hover:text-white transition-colors"
                      data-testid="detail-join-btn"
                    >
                      List your business
                    </button>
                  </div>
                </motion.div>
              </div>

              {related.length > 0 && (
                <section className="mt-24">
                  <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-ink">
                    Related <span className="italic text-brand-wine">businesses</span>
                  </h2>
                  <div className="mt-8 grid md:grid-cols-3 gap-6">
                    {related.map((b) => (
                      <Link
                        to={`/business/${b.id}`}
                        key={b.id}
                        className="group bg-white rounded-2xl overflow-hidden border border-brand-line hover:-translate-y-1 hover:shadow-[0_20px_40px_-25px_rgba(139,30,63,0.25)] transition-all duration-500"
                        data-testid={`related-business-${b.id}`}
                      >
                        <div className="aspect-[4/3] overflow-hidden">
                          <img src={b.image} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="p-5">
                          <span className="text-[11px] uppercase tracking-widest text-brand-muted">{b.tag}</span>
                          <h3 className="font-serif text-xl font-semibold text-brand-ink mt-1">{b.name}</h3>
                          <p className="text-sm text-brand-muted mt-2 line-clamp-2">{b.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>

      <Footer onNav={() => {}} />
      <JoinDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
